import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { Region } from "@shared/schema";

const baseLeadSchema = z.object({
  leadName: z.string().optional(),
  contactName: z.string().optional(),
  phoneNumber: z.string().optional(),
  mobilePhone: z.string().optional(),
  emailAddress: z.string().email({ message: "Invalid email address" }).optional().or(z.literal("")),
  regionId: z.string().min(1, { message: "Region is required" }),
  clientType: z.string().optional(),
});

export type AddLeadFormValues = z.infer<typeof baseLeadSchema>;

interface AddLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regions: Region[];
  clientTypeOptions: string[];
  onSubmit: (data: AddLeadFormValues) => void;
  isPending: boolean;
  defaultRegionId?: string;
}

export function AddLeadModal({
  open,
  onOpenChange,
  regions,
  clientTypeOptions,
  onSubmit,
  isPending,
  defaultRegionId,
}: AddLeadModalProps) {
  const accountNameRef = useRef<HTMLInputElement>(null);

  const addLeadSchema = useMemo(() =>
    baseLeadSchema.extend({
      clientType: clientTypeOptions.length > 0
        ? z.string().min(1, { message: "Client Type is required" })
        : z.string().optional(),
    }),
    [clientTypeOptions.length]
  );

  const form = useForm<AddLeadFormValues>({
    resolver: zodResolver(addLeadSchema),
    defaultValues: {
      leadName: "",
      contactName: "",
      phoneNumber: "",
      mobilePhone: "",
      emailAddress: "",
      regionId: defaultRegionId || "",
      clientType: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        leadName: "",
        contactName: "",
        phoneNumber: "",
        mobilePhone: "",
        emailAddress: "",
        regionId: defaultRegionId || (regions[0]?.id ?? ""),
        clientType: "",
      });
      setTimeout(() => accountNameRef.current?.focus(), 50);
    }
  }, [open, defaultRegionId, regions]);

  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Lead</DialogTitle>
          <DialogDescription>
            {clientTypeOptions.length > 0
              ? "Fill in lead details. Region and Client Type are required."
              : "Fill in lead details. Region is required."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <FormField
              control={form.control}
              name="leadName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      ref={(el) => {
                        field.ref(el);
                        (accountNameRef as any).current = el;
                      }}
                      placeholder="Account name"
                      autoComplete="off"
                      data-testid="input-lead-account-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Contact name"
                      autoComplete="off"
                      data-testid="input-lead-contact-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="tel"
                        placeholder="(555) 000-0000"
                        autoComplete="off"
                        data-testid="input-lead-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mobilePhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="tel"
                        placeholder="(555) 000-0000"
                        autoComplete="off"
                        data-testid="input-lead-mobile"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="emailAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="email@example.com"
                      autoComplete="off"
                      data-testid="input-lead-email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="regionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Region <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger data-testid="select-lead-region">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {regions.map((r) => (
                        <SelectItem key={r.id} value={r.id} data-testid={`option-region-${r.id}`}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {clientTypeOptions.length > 0 && (
              <FormField
                control={form.control}
                name="clientType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                    Client Type <span className="text-destructive">*</span>
                  </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid="select-lead-client-type">
                          <SelectValue placeholder="Select client type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {clientTypeOptions.map((ct) => (
                          <SelectItem key={ct} value={ct} data-testid={`option-client-type-${ct}`}>
                            {ct}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <DialogFooter className="pt-2 gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel-add-lead"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                data-testid="button-submit-add-lead"
              >
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Lead
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
